import { useEffect, useMemo, useRef } from 'react'

declare var Chart: any;

export interface CroppingYearData {
    labels: string[],
    datasets: [{
        data: number[],
        backgroundColor: any[],
        label: string
    }]
}

interface Props {
    title: string
    yearData?: CroppingYearData
}

export function CroppingYearChartComponent({ title, yearData }: Props) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const chartInstanceRef = useRef<any>(null);

    const chartConfig = useMemo(() => { 
        return {
        type: 'pie',
        data: yearData,
        options: {
            tooltips: {
                enabled: false,
                // NOTE: this creates a tooltip which is just a bit better looking than the standard 'enabled: true' tooltip, so maybe we can get rid of this when migrating to the new version of ChartJs
                custom: function(tooltipModel) {
                    var tooltipEl = document.getElementById('chartjs-tooltip');

                    // Create element on first render
                    if (!tooltipEl) {
                        tooltipEl = document.createElement('div');
                        tooltipEl.id = 'chartjs-tooltip';
                        tooltipEl.innerHTML = "<table></table>"
                        document.body.appendChild(tooltipEl);
                    }

                    // Hide if no tooltip
                    if (tooltipModel.opacity === 0) {
                        tooltipEl.style.opacity = '0';
                        return;
                    }

                    // Set caret Position
                    tooltipEl.classList.remove('above', 'below', 'no-transform');
                    if (tooltipModel.yAlign) {
                        tooltipEl.classList.add(tooltipModel.yAlign);
                    } else {
                        tooltipEl.classList.add('no-transform');
                    }

                    // Set Text
                    if (tooltipModel.body) {
                        var titleLines = tooltipModel.title || [];
                        var bodyLines = tooltipModel.body.map(b => b.lines);

                        var innerHtml = '<thead>';

                        titleLines.forEach((title) => {
                            innerHtml += '<tr><th>' + title + '</th></tr>';
                        });
                        innerHtml += '</thead><tbody>';

                        bodyLines.forEach((body, i) => {
                            var colors = tooltipModel.labelColors[i];
                            var style = 'background:' + colors.backgroundColor;
                            style += '; border-color:' + colors.borderColor;
                            style += '; border-width: 1px; border-style: solid';
                            var span = '<span class="chartjs-tooltip-key" style="' + style + '"></span>';
                            innerHtml += '<tr><td>' + span + body + ' ha</td></tr>';
                        });
                        innerHtml += '</tbody>';

                        var tableRoot = tooltipEl.querySelector('table');
                        tableRoot.innerHTML = innerHtml;
                    }

                    // `this` will be the overall tooltip
                    var position = (this as any)._chart.canvas.getBoundingClientRect();

                    // Display, position, and set styles for font
                    tooltipEl.style.opacity = '1';
                    tooltipEl.style.left = position.left + tooltipModel.caretX + 'px';
                    tooltipEl.style.top = position.top + tooltipModel.caretY + 'px';
                    tooltipEl.style.fontSize = tooltipModel.fontSize;
                    tooltipEl.style.fontStyle = tooltipModel._fontStyle;
                    tooltipEl.style.padding = tooltipModel.yPadding + 'px ' + tooltipModel.xPadding + 'px';

                }
            },
            legend: {
                display: false
            },
            responsive: false
        }
    }}, [yearData]);

    useEffect(() => {
        const ctx = canvasRef.current?.getContext("2d");
        if (ctx) {
            chartInstanceRef.current?.destroy();
            chartInstanceRef.current = new Chart(ctx, chartConfig);
        }

        return () => {
            chartInstanceRef.current?.destroy();
        }
    }, [yearData, chartConfig]);

    return (
        <div className="cropping-year-chart">
            <h4>{title}</h4>
            <canvas ref={canvasRef} id="fieldsPie" width="200" height="200"></canvas>
            <div className="legend">
                <ul>
                    {yearData?.datasets[0].data.map((c, index) => {
                        return <li key={index}>
                            <span className="omnia-crop-circle" 
                                style={{ backgroundColor: yearData?.datasets[0].backgroundColor[index] }}></span>
                            &nbsp;{yearData?.labels[index]} ({c} ha)
                        </li>
                    })}
                </ul>
            </div>
        </div>
    );
}